import type { DataModelView } from '../api/dataModelsService';
import type { PhysicalTable } from '../components/data-model/types';

/** Display an exact grain-column label without rewriting identifiers or custom grain text. */
export function semanticViewGrainDisplayName(
  view: Pick<DataModelView, 'grain' | 'columnsJson'>,
): string | null {
  if (!view.grain || !Array.isArray(view.columnsJson)) return view.grain;
  for (const column of view.columnsJson) {
    if (!column || typeof column !== 'object') continue;
    if ('name' in column && column.name === view.grain
      && 'label' in column && typeof column.label === 'string' && column.label.trim()) {
      return column.label;
    }
  }
  return view.grain;
}

const humanize = (name: string): string => name.replace(/[_-]+/g, ' ')
  .replace(/\b\w\S*/g, word => word[0].toUpperCase() + word.slice(1).toLowerCase());
// Python str.title(), used by the deterministic dimension/multi-fact builders.
const pythonTitle = (name: string): string => name.replace(/[a-z]+/gi,
  word => word[0].toUpperCase() + word.slice(1).toLowerCase());

/** Known deterministic title recipes, not a heuristic for arbitrary business titles. */
function generatedTitles(view: DataModelView): string[] {
  return [
    view.name,
    humanize(view.name),
    humanize(view.name.replace(/_fact_view$/, '')),
    humanize(view.name.replace(/(_fact_view|_view|_fact)$/, '')),
    pythonTitle(view.name.replace(/_/g, ' ').replace(/ dim$/, '')),
    pythonTitle(view.name.replace(/(_fact_view|_view|_fact)$/, '').replace(/_/g, ' ').trim()),
  ];
}

/** Read-only projection for existing models. SQL, names, IDs and saved titles stay untouched. */
export function semanticViewDisplayNames(
  views: DataModelView[], tables: Pick<PhysicalTable, 'name' | 'aliasName' | 'displayName'>[],
): DataModelView[] {
  const byName = new Map(views.map(view => [view.name, view]));
  const tableLabels = new Map<string, string>();
  for (const table of tables) {
    const label = table.aliasName || table.displayName || table.name;
    if (label === table.name) continue;
    tableLabels.set(table.name, label);
    if (table.aliasName) tableLabels.set(table.aliasName, label);
  }
  const resolved = new Map<string, string | null>();
  const visiting = new Set<string>();
  const resolve = (view: DataModelView): string | null => {
    if (resolved.has(view.name)) return resolved.get(view.name)!;
    if (visiting.has(view.name)) return view.title;
    visiting.add(view.name);
    let label: string | undefined;
    const candidates = generatedTitles(view);
    if (view.viewType === 'multi_fact') {
      const children = view.baseTables.map(name => byName.get(name));
      if (children.length === 2 && children.every(child => child !== undefined)) {
        const [a, b] = children as [DataModelView, DataModelView];
        for (const first of generatedTitles(a)) {
          for (const second of generatedTitles(b)) {
            candidates.push(`${first} + ${second}`, `${second} + ${first}`);
          }
        }
        const aTitle = resolve(a);
        const bTitle = resolve(b);
        if (aTitle && bTitle && (aTitle !== a.title || bTitle !== b.title
          || tableLabels.has(a.factTable || '') || tableLabels.has(b.factTable || ''))) {
          label = `${aTitle} + ${bTitle}`;
        }
      }
    } else {
      const source = view.factTable || (view.baseTables.length === 1 ? view.baseTables[0] : undefined);
      if (source) label = tableLabels.get(source);
    }
    const title = label && (!view.title || candidates.includes(view.title)) ? label : view.title;
    visiting.delete(view.name);
    resolved.set(view.name, title);
    return title;
  };
  return views.map(view => ({ ...view, title: resolve(view) }));
}